import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { greeting } from "../portfolio";
import { FiVolumeX } from "react-icons/fi";
import {
  isSoundEnabled,
  toggleSound,
  subscribeSoundState,
  playHover,
  playClick,
} from "../utils/soundEngine";

const navLinks = [
  { id: "hero", title: "Home" },
  { id: "skills", title: "Skills" },
  { id: "experience", title: "Experience" },
  { id: "projects", title: "Projects" },
  { id: "education", title: "Education" },
  { id: "blogs", title: "Writing" },
  { id: "awards", title: "Awards" },
  { id: "contact", title: "Contact" },
];

function SoundToggle() {
  const [soundOn, setSoundOn] = useState(isSoundEnabled());

  useEffect(() => {
    return subscribeSoundState((val) => setSoundOn(val));
  }, []);

  return (
    <button
      onClick={() => toggleSound()}
      onMouseEnter={() => playHover()}
      aria-label="Toggle audio effects"
      title={soundOn ? "Mute audio" : "Enable atmospheric audio & UI haptics"}
      className="w-8 h-8 rounded-full flex items-center justify-center text-fg-faint hover:text-fg bg-surface/[0.04] hover:bg-surface/[0.08] border border-surface/[0.06] transition-all duration-300 relative group"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={soundOn ? "sound-on" : "sound-off"}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex items-center justify-center"
        >
          {soundOn ? (
            <span className="flex items-center gap-[1.5px] h-3 px-0.5">
              <span className="w-[2px] h-3 bg-emerald-400 rounded-full animate-[pulse_0.8s_ease-in-out_infinite]" />
              <span className="w-[2px] h-2 bg-emerald-400 rounded-full animate-[pulse_0.6s_ease-in-out_infinite_0.2s]" />
              <span className="w-[2px] h-3.5 bg-emerald-400 rounded-full animate-[pulse_0.9s_ease-in-out_infinite_0.4s]" />
            </span>
          ) : (
            <FiVolumeX
              size={13}
              className="text-fg-faint group-hover:text-fg"
            />
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      const sections = navLinks.map((link) => ({
        id: link.id,
        el: document.getElementById(link.id),
      }));

      for (let i = sections.length - 1; i >= 0; i--) {
        if (
          sections[i].el &&
          sections[i].el.getBoundingClientRect().top <= 150
        ) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out-expo ${
        scrolled
          ? "py-3 backdrop-blur-xl bg-bg/[.8] border-b border-surface/[0.06]"
          : "py-5 bg-transparent"
      }`}
    >
      <nav className="container-main flex items-center justify-between">
        <button
          onClick={() => {
            playClick();
            scrollTo("hero");
          }}
          onMouseEnter={() => playHover()}
          className="relative z-[60]"
        >
          <span className="font-display text-base font-bold tracking-tight text-fg hover:text-fg-muted transition-colors duration-300">
            {greeting.logo_name}
          </span>
        </button>

        <div className="hidden md:flex items-center gap-1">
          <ul className="flex items-center gap-1">
            {navLinks.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => {
                    playClick();
                    scrollTo(link.id);
                  }}
                  onMouseEnter={() => playHover()}
                  className={`relative px-3 py-1.5 rounded-full text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    activeSection === link.id
                      ? "text-fg font-semibold"
                      : "text-fg-faint hover:text-fg-muted"
                  }`}
                >
                  {link.title}
                  {activeSection === link.id && (
                    <motion.span
                      layoutId="active-nav-pill"
                      className="absolute inset-0 rounded-full bg-surface/[0.08] border border-surface/[0.12] -z-10 shadow-sm"
                      transition={{
                        type: "spring",
                        stiffness: 420,
                        damping: 32,
                        mass: 0.8,
                      }}
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="ml-3 flex items-center gap-1.5">
            <SoundToggle />
          </div>
        </div>

        <div className="flex items-center gap-2 md:hidden relative z-[60]">
          <SoundToggle />

          <button
            onClick={() => {
              playClick();
              setMenuOpen(!menuOpen);
            }}
            className="p-2 -mr-2"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-4 flex flex-col justify-between">
              <span
                className={`block h-px bg-fg rounded-full transition-all duration-300 origin-center ${
                  menuOpen ? "rotate-45 translate-y-[7px]" : ""
                }`}
              />
              <span
                className={`block h-px bg-fg rounded-full transition-all duration-300 ${
                  menuOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`block h-px bg-fg rounded-full transition-all duration-300 origin-center ${
                  menuOpen ? "-rotate-45 -translate-y-[7px]" : ""
                }`}
              />
            </div>
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 md:hidden bg-bg/[.98] backdrop-blur-2xl flex flex-col items-center justify-center"
            >
              <nav className="flex flex-col items-center gap-6">
                {navLinks.map((link, i) => (
                  <motion.button
                    key={link.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 + 0.1 }}
                    onClick={() => {
                      playClick();
                      scrollTo(link.id);
                    }}
                    onMouseEnter={() => playHover()}
                    className={`text-2xl font-display font-semibold tracking-tight transition-colors duration-300 ${
                      activeSection === link.id
                        ? "text-fg"
                        : "text-fg-faint hover:text-fg-muted"
                    }`}
                  >
                    {link.title}
                  </motion.button>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}

export default Navbar;
